import React, { useState, useEffect } from "react";
import {
  FiFilter,
  FiCheck,
  FiX,
  FiClock,
  FiUser,
  FiPhone,
  FiMail,
  FiDollarSign,
  FiCalendar,
  FiRefreshCw,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { 
  getWithdrawPending, 
  withdrawapprovestatuschange,
  withdrawrejectstatuschange
} from "../../api/endpoints";
import Swal from "../../utils/swalCompat";

import PageHeader from "../../components/PageHeader";

const WithdrawPending = () => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [actionLoading, setActionLoading] = useState(null);

  // ✅ Fetch Pending Withdrawals
  const fetchPendingWithdrawals = async () => {
    try {
      setLoading(true);
      const params = {
        page,
        limit,
        search: search.trim(),
        startDate,
        endDate,
      };

      const res = await getWithdrawPending(params);
      console.log("Pending Withdrawals:", res);

      // ✅ Proper data extraction from response
      const responseData = res.data || res;
      setTransactions(responseData.transactions || []);
      setTotal(responseData.total || 0);
      setTotalPages(responseData.totalPages || 1);
    } catch (error) {
      console.error("Error fetching pending withdrawals:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: error.response?.data?.message || "Failed to fetch pending withdrawals",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPendingWithdrawals();
  }, [page, search, startDate, endDate]);

  // ✅ Handle Approve
  const handleApprove = async (id) => {
    const result = await Swal.fire({
      title: "Approve Withdrawal?",
      text: "Are you sure you want to approve this withdrawal?",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#28a745",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, Approve!",
      cancelButtonText: "Cancel",
    });

    if (result.isConfirmed) {
      try {
        setActionLoading(id);
        const res = await withdrawapprovestatuschange({ withdrawId: id });
        console.log("Approve Response:", res);

        Swal.fire({
          icon: "success",
          title: "Approved!",
          text: "Withdrawal approved successfully",
          timer: 2000,
          showConfirmButton: false,
        });

        fetchPendingWithdrawals();
      } catch (error) {
        console.error("Error approving withdrawal:", error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: error.response?.data?.message || "Failed to approve withdrawal",
        });
      } finally {
        setActionLoading(null);
      }
    }
  };

  // ✅ Handle Reject
  const handleReject = async (id) => {
    const { value: reason } = await Swal.fire({
      title: "Reject Withdrawal",
      input: "textarea",
      inputLabel: "Reason for Rejection",
      inputPlaceholder: "Type your reason here... (e.g. Invalid bank details)",
      inputAttributes: {
        "aria-label": "Type your reason here"
      },
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, Reject!",
      cancelButtonText: "Cancel",
      inputValidator: (value) => {
        if (!value) {
          return "You need to write a reason for rejection!";
        }
      }
    });

    if (reason === undefined) return;

    if (reason) {
      const confirmResult = await Swal.fire({
        title: "Are you sure?",
        text: `You are rejecting this withdrawal. Reason: "${reason}"`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#d33",
        cancelButtonColor: "#6c757d",
        confirmButtonText: "Yes, Reject!",
        cancelButtonText: "Cancel",
      });

      if (confirmResult.isConfirmed) {
        try {
          setActionLoading(id);
          const res = await withdrawrejectstatuschange({ 
            withdrawId: id, 
            rejectedReason: reason 
          });
          console.log("Reject Response:", res);

          Swal.fire({
            icon: "success",
            title: "Rejected!",
            text: "Withdrawal rejected successfully",
            timer: 2000,
            showConfirmButton: false,
          });

          fetchPendingWithdrawals();
        } catch (error) {
          console.error("Error rejecting withdrawal:", error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: error.response?.data?.message || "Failed to reject withdrawal",
          });
        } finally {
          setActionLoading(null);
        }
      }
    }
  };

  // ✅ Format Date
  const formatDate = (date) => {
    if (!date) return "N/A";
    return new Date(date).toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // ✅ Format Currency
  const formatCurrency = (amount) => {
    if (amount === null || amount === undefined) return "₹ 0";
    return `₹ ${Number(amount).toLocaleString("en-IN")}`;
  };

  // ✅ Get Status Badge
  const getStatusBadge = (status) => {
    const statusMap = {
      PENDING: { color: "badge bg-warning text-dark", icon: <FiClock />, label: "Pending" },
      APPROVED: { color: "badge bg-success", icon: <FiCheck />, label: "Approved" },
      REJECTED: { color: "badge bg-danger", icon: <FiX />, label: "Rejected" },
    };
    const statusInfo = statusMap[status] || statusMap.PENDING;
    return (
      <span className={`${statusInfo.color} d-inline-flex align-items-center gap-1`}>
        {statusInfo.icon} {statusInfo.label}
      </span>
    );
  };

  // ✅ Payment Method Display
  const getPaymentDisplay = (item) => {
    if (item.withdrawType === "UPI") {
      return (
        <div className="d-flex flex-column small">
          <span className="badge bg-primary mb-1">UPI</span>
          <span className="text-muted">{item.upiId || "N/A"}</span>
        </div>
      );
    }
    if (item.withdrawType === "BANK") {
      return (
        <div className="d-flex flex-column small">
          <span className="badge bg-info mb-1">BANK</span>
          <span className="fw-bold">{item.accountHolderName || "N/A"}</span>
          <span className="text-muted">A/C: {item.accountNumber || "N/A"}</span>
          <span className="text-muted">IFSC: {item.ifscCode || "N/A"}</span>
        </div>
      );
    }
    return <span className="text-muted">{item.withdrawType || "N/A"}</span>;
  };

  // ✅ Display User Name properly
  const getUserDisplayName = (item) => {
    if (item.userName && item.userName.trim() !== "") {
      return item.userName;
    }
    if (item.userMobile) {
      return `User (${item.userMobile})`;
    }
    return "Unknown User";
  };

  return (
    <div className="lead-page">
      <div className="withdraw-pending-page">
        {/* Header */}
        <PageHeader
          eyebrow="Withdraw Payment"
          title="Withdraw Pending"
          subtitle="Manage and approve pending withdrawal requests."
          icon={<FiClock />}
          tone="warning"
          stat={{ label: "Total Pending", value: total }}
        />

        {/* Card */}
        <div className="card shadow-sm border-0">
          {/* Card Header */}
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap border-bottom">
            <h5 className="mb-0 fw-bold">
              <FiClock className="me-2" />
              Pending Withdrawal Requests
            </h5>
            <div className="d-flex gap-2 align-items-center flex-wrap">
              {showFilter && (
                <>
                  <input
                    type="text"
                    className="form-control form-control-sm"
                    placeholder="Search by User, Mobile..."
                    value={search}
                    onChange={(e) => {
                      setPage(1);
                      setSearch(e.target.value);
                    }}
                    style={{ width: "200px" }}
                  />
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={startDate}
                    onChange={(e) => {
                      setPage(1);
                      setStartDate(e.target.value);
                    }}
                    style={{ width: "140px" }}
                  />
                  <input
                    type="date"
                    className="form-control form-control-sm"
                    value={endDate}
                    onChange={(e) => {
                      setPage(1);
                      setEndDate(e.target.value);
                    }}
                    style={{ width: "140px" }}
                  />
                </>
              )}
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setShowFilter(!showFilter)}
              >
                <FiFilter /> Filter
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={fetchPendingWithdrawals}
                disabled={loading}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm" />
                ) : (
                  <>
                    <FiRefreshCw /> Refresh
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Card Body */}
          <div className="card-body p-0">
            <div className="table-responsive">
              <table className="table table-hover mb-0">
                <thead className="table-light">
                  <tr>
                    <th style={{ width: "50px" }}>#</th>
                    <th style={{ minWidth: "220px" }}>
                      <div className="d-flex flex-column">
                        <span>User Details</span>
                        <small className="fw-normal text-muted">Mobile / Referral</small>
                      </div>
                    </th>
                    <th style={{ width: "120px" }}>Deposit Bal.</th>
                    <th style={{ width: "120px" }}>Winning Bal.</th>
                    <th style={{ width: "130px" }}>Withdraw Amount</th>
                    <th style={{ minWidth: "200px" }}>Payment Details</th>
                    <th style={{ minWidth: "150px" }}>Date</th>
                    <th style={{ width: "110px" }}>Status</th>
                    <th style={{ width: "200px" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                        <div className="mt-2 text-muted">Loading withdrawals...</div>
                      </td>
                    </tr>
                  ) : transactions.length > 0 ? (
                    transactions.map((item, index) => (
                      <tr key={item._id}>
                        <td className="fw-bold">{(page - 1) * limit + index + 1}</td>

                        {/* User Details */}
                        <td>
                          <div className="d-flex flex-column">
                            <div className="d-flex align-items-center gap-1">
                              <FiUser size={12} className="text-primary" />
                              <strong>{getUserDisplayName(item)}</strong>
                            </div>
                            <div className="text-muted small">
                              <FiPhone size={10} /> {item.userMobile || "N/A"}
                            </div>
                            {item.userEmail && (
                              <div className="text-muted small">
                                <FiMail size={10} /> {item.userEmail}
                              </div>
                            )}
                            <div className="text-muted small">
                              <span className="badge bg-light text-dark">
                                Ref: {item.referralCode || "-"}
                              </span>
                            </div>
                          </div>
                        </td>

                        {/* Wallet Balances */}
                        <td>
                          <span className="fw-bold text-success">
                            {formatCurrency(item.depositBalance)}
                          </span>
                        </td>
                        <td>
                          <span className="fw-bold text-primary">
                            {formatCurrency(item.winningBalance)}
                          </span>
                        </td>

                        {/* Withdraw Amount */}
                        <td>
                          <span className="fw-bold text-danger fs-5">
                            {formatCurrency(item.amount)}
                          </span>
                        </td>

                        {/* Payment Details */}
                        <td>{getPaymentDisplay(item)}</td>

                        {/* Date */}
                        <td>
                          <div className="small">
                            <FiCalendar size={10} className="me-1" />
                            {formatDate(item.createdAt)}
                          </div>
                          <div className="small text-muted">
                            Order: {item.orderId || "N/A"}
                          </div>
                        </td>

                        {/* Status */}
                        <td>{getStatusBadge(item.status)}</td>

                        {/* Action */}
                        <td>
                          <div className="d-flex gap-1">
                            <button
                              className="btn btn-success btn-sm px-3"
                              onClick={() => handleApprove(item._id)}
                              disabled={actionLoading === item._id}
                            >
                              {actionLoading === item._id ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                <>
                                  <FiCheck /> Approve
                                </>
                              )}
                            </button>
                            <button
                              className="btn btn-danger btn-sm px-3"
                              onClick={() => handleReject(item._id)}
                              disabled={actionLoading === item._id}
                            >
                              {actionLoading === item._id ? (
                                <span className="spinner-border spinner-border-sm" />
                              ) : (
                                <>
                                  <FiX /> Reject
                                </>
                              )}
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="9" className="text-center py-5">
                        <div className="text-muted">
                          <FiClock size={40} className="mb-3 d-block mx-auto text-secondary" />
                          <h5 className="fw-bold">No Pending Withdrawals</h5>
                          <p className="small">All withdrawal requests have been processed</p>
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="d-flex justify-content-between align-items-center p-3 border-top">
                <div>
                  <span className="text-muted small">
                    Showing {(page - 1) * limit + 1} to{" "}
                    {Math.min(page * limit, total)} of {total} entries
                  </span>
                </div>
                <div className="d-flex gap-2 align-items-center">
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === 1}
                    onClick={() => setPage(page - 1)}
                  >
                    <FiChevronLeft /> Previous
                  </button>
                  <span className="fw-bold px-3">
                    Page {page} of {totalPages}
                  </span>
                  <button
                    className="btn btn-outline-secondary btn-sm"
                    disabled={page === totalPages}
                    onClick={() => setPage(page + 1)}
                  >
                    Next <FiChevronRight />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WithdrawPending;